/* SSi website – Welsh landing page closing sections.
 *
 * Three calmer, copy-led pieces that sit between Ruth Jones and the
 * news/footer, written for the Welsh page specifically:
 *
 *   <WelshSomethingBigger />
 *     Sits just under the Ruth Jones testimonial. Names the bigger
 *     thing the learner is joining – language activism, community,
 *     reverse-language-shift. Cream background, no card chrome.
 *
 *   <WelshClosing />
 *     The "Welsh has been waiting for you" closer. Lives below the FAQ.
 *     Two short paragraphs, large display type, generous whitespace.
 *     Anchored by the same primary/secondary CTAs as the hero.
 *
 *   <WelshTrialFlow />
 *     The "What happens when you click 'Start your 7 days free'"
 *     numbered explainer. Cream card with a brand-red top bar; sits
 *     under <WelshClosing />.
 *
 * All copy is editable through the Tweaks panel via cy* keys.
 */
import { Link } from 'react-router-dom';

export function WelshSomethingBigger({ t }) {
  return (
    <section className="ssi-cyBigger" style={cyClosersStyles.biggerWrap}>
      <div style={cyClosersStyles.biggerInner}>
        <div style={cyClosersStyles.kicker}>{t.cyBiggerKicker}</div>
        <h2 style={cyClosersStyles.biggerH2}>{t.cyBiggerTitle}</h2>
        <p style={cyClosersStyles.biggerP}>{t.cyBiggerP1}</p>
        <p style={cyClosersStyles.biggerP}>{t.cyBiggerP2}</p>
        <p style={{...cyClosersStyles.biggerP, ...cyClosersStyles.biggerCoda}}>{t.cyBiggerP3}</p>
      </div>
    </section>
  );
}

export function WelshClosing({ t }) {
  return (
    <section className="ssi-cyClosing" style={cyClosersStyles.closingWrap}>
      <div style={cyClosersStyles.closingInner}>
        <h2 style={cyClosersStyles.closingH2}>{t.cyClosingTitle}</h2>
        <p style={cyClosersStyles.closingP}>{t.cyClosingP1}</p>
        <p style={cyClosersStyles.closingP}>{t.cyClosingP2}</p>
        <div style={cyClosersStyles.closingCtas}>
          <Link to="/pay?course=en-cy-south" className="btn btn--primary">{t.cyClosingCta1}</Link>
          <a href="#sample" className="btn btn--ghost">{t.cyClosingCta2}</a>
        </div>
      </div>
    </section>
  );
}

export function WelshTrialFlow({ t }) {
  const steps = [
    { title: t.cyFlowStep1Title, body: t.cyFlowStep1Body },
    { title: t.cyFlowStep2Title, body: t.cyFlowStep2Body },
    { title: t.cyFlowStep3Title, body: t.cyFlowStep3Body },
  ];
  return (
    <section className="ssi-cyFlow" style={cyClosersStyles.flowWrap}>
      <div style={cyClosersStyles.flowInner}>
        <div style={cyClosersStyles.flowCard}>
          <div style={cyClosersStyles.flowKicker}>{t.cyFlowKicker}</div>
          <ol style={cyClosersStyles.flowList}>
            {steps.map((s, i) => (
              <li key={i} style={cyClosersStyles.flowItem}>
                <span style={cyClosersStyles.flowNum}>{String(i + 1).padStart(2, '0')}</span>
                <div style={cyClosersStyles.flowText}>
                  <div style={cyClosersStyles.flowTitle}>{s.title}</div>
                  <p style={cyClosersStyles.flowBody}>{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

const cyClosersStyles = {
  /* ─── shared ─── */
  kicker: {
    fontSize: 12, letterSpacing: '.16em', textTransform: 'uppercase',
    color: 'var(--brandRed)', fontWeight: 700, marginBottom: 14,
  },

  /* ─── Something bigger ─── */
  biggerWrap: {
    background: 'var(--cream, #FBF7F0)',
    padding: '96px 32px',
  },
  biggerInner: {
    maxWidth: 760, margin: '0 auto',
  },
  biggerH2: {
    fontFamily: 'var(--font-display)', fontWeight: 700,
    fontSize: 44, lineHeight: 1.1,
    letterSpacing: '-.015em',
    margin: '0 0 22px',
    textWrap: 'balance',
  },
  biggerP: {
    fontFamily: 'var(--font-body)',
    fontSize: 18, lineHeight: 1.6,
    color: 'var(--fg-1)',
    margin: '0 0 18px',
    textWrap: 'pretty',
    maxWidth: '64ch',
  },
  biggerCoda: {
    fontStyle: 'italic',
    color: 'var(--grey)',
    marginBottom: 0,
  },

  /* ─── Welsh has been waiting for you ─── */
  closingWrap: {
    background: '#fff',
    padding: '120px 32px 96px',
    borderTop: '1px solid rgba(73,3,0,.1)',
  },
  closingInner: {
    maxWidth: 880, margin: '0 auto',
    textAlign: 'left',
  },
  closingH2: {
    fontFamily: 'var(--font-display)', fontWeight: 700,
    fontSize: 64, lineHeight: 1.04,
    letterSpacing: '-.02em',
    margin: '0 0 28px',
    color: 'var(--deepRed, #490300)',
    textWrap: 'balance',
  },
  closingP: {
    fontFamily: 'var(--font-body)',
    fontSize: 19, lineHeight: 1.6,
    color: 'var(--fg-1)',
    margin: '0 0 18px',
    textWrap: 'pretty',
    maxWidth: '60ch',
  },
  closingCtas: {
    display: 'flex', gap: 12,
    marginTop: 28,
    flexWrap: 'wrap',
  },

  /* ─── What happens when you click ─── */
  flowWrap: {
    background: '#fff',
    padding: '0 32px 96px',
  },
  flowInner: {
    maxWidth: 880, margin: '0 auto',
  },
  flowCard: {
    background: 'var(--cream, #FBF7F0)',
    border: '1px solid rgba(73,3,0,.14)',
    borderTop: '4px solid var(--brandRed)',
    borderRadius: 8,
    padding: '32px 36px 36px',
  },
  flowKicker: {
    fontSize: 12, letterSpacing: '.16em', textTransform: 'uppercase',
    color: 'var(--brandRed)', fontWeight: 700, marginBottom: 18,
  },
  flowList: {
    listStyle: 'none',
    margin: 0, padding: 0,
    display: 'flex', flexDirection: 'column', gap: 22,
  },
  flowItem: {
    display: 'grid',
    gridTemplateColumns: '52px 1fr',
    gap: 18,
    alignItems: 'baseline',
  },
  flowNum: {
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: 32,
    lineHeight: 1,
    color: 'var(--brandRed)',
    letterSpacing: '-.02em',
  },
  flowText: {},
  flowTitle: {
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: 19,
    lineHeight: 1.25,
    color: 'var(--fg-1)',
    margin: '0 0 6px',
    letterSpacing: '-.005em',
  },
  flowBody: {
    fontFamily: 'var(--font-body)',
    fontSize: 16, lineHeight: 1.55,
    color: 'var(--fg-1)',
    margin: 0,
    textWrap: 'pretty',
  },
};
